import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { LuCircleX, LuGripVertical, LuInfo } from 'react-icons/lu'

import {
  FieldError,
  FieldErrorMessage,
  Input,
  InputWrapper,
  Label,
} from '@/lib/core/components/TextField/TextField.styles'
import { Tooltip } from '@/lib/core/components/Tooltip'

import { TaskLocationPopout } from '../TaskLocationPopout'
import styles from './SortableLocationRow.module.css'
import { SortableLocationRowProps } from './SortableLocationRow.types'
import { getStopLabel } from './SortableLocationRow.utils'

export const SortableLocationRow = ({
  stop,
  index,
  total,
  canRemove,
  onLocationChange,
  onDatetimeChange,
  onRemove,
  locationError,
  locationWarning,
  datetimeError,
}: SortableLocationRowProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: stop.id,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1 : 0,
  }

  const label = getStopLabel(index, total)
  const isEndpoint = index === 0 || index === total - 1

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={styles.locationRow}
      data-testid={`location-row-${index}`}
    >
      <div className={styles.locationRowLeft}>
        <Tooltip trigger="mouseenter" placement="left" tooltipContent="Drag to re-order">
          <button
            type="button"
            className={styles.dragHandle}
            {...attributes}
            {...listeners}
            data-testid={`drag-handle-${index}`}
          >
            <LuGripVertical size={16} />
          </button>
        </Tooltip>
        <div className={styles.stopBadge} data-testid={`stop-badge-${index}`}>
          {index + 1}
        </div>
      </div>

      <div className={styles.locationRowFields}>
        <div className={styles.locationField}>
          <Label $required htmlFor={`location-${stop.id}`}>
            {label} Location
          </Label>
          <TaskLocationPopout
            key={`location-popout-${stop.id}`}
            mode="CREATE"
            locationInput={stop.location ?? undefined}
            onUpdated={(selectedLocation) => {
              if (selectedLocation) {
                onLocationChange(stop.id, selectedLocation)
              }
            }}
            placeholder="Select location"
            testId={`location-field-${index}`}
            error={locationError}
          />
          {locationWarning && !locationError && (
            <div className={styles.locationWarning} data-testid={`location-warning-${index}`}>
              <LuInfo size={14} />
              {locationWarning}
            </div>
          )}
        </div>

        <div className={styles.datetimeField}>
          <Label $required={isEndpoint} htmlFor={`datetime-${stop.id}`}>
            Date / Time
          </Label>
          <InputWrapper>
            <Input
              id={`datetime-${stop.id}`}
              name={`datetime-${stop.id}`}
              type="datetime-local"
              value={stop.datetime}
              $hasError={!!datetimeError}
              onChange={(e) => onDatetimeChange(stop.id, e.target.value)}
              data-testid={`datetime-field-${index}-input`}
            />
          </InputWrapper>
          <FieldError>
            {datetimeError && (
              <FieldErrorMessage data-testid={`datetime-field-${index}-error`}>
                {datetimeError}
              </FieldErrorMessage>
            )}
          </FieldError>
        </div>
      </div>

      <Tooltip trigger={'mouseenter'} tooltipContent="Remove stop" placement="right">
        <button
          type="button"
          className={styles.removeButton}
          onClick={() => canRemove && onRemove(stop.id)}
          style={canRemove ? undefined : { visibility: 'hidden' }}
          data-testid={`remove-stop-${index}`}
        >
          <LuCircleX size={22} />
        </button>
      </Tooltip>
    </div>
  )
}
